import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import api from '../lib/api';

export function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const runSearch = (value) => {
    clearTimeout(timerRef.current);
    if (value.trim().length < 2) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/search', { params: { q: value } });
        setGroups(r.data.groups);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const go = (link) => {
    setOpen(false);
    setQ('');
    setGroups([]);
    navigate(link);
  };

  return (
    <div ref={boxRef} className="relative hidden lg:block w-80">
      <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-magenta/30">
        {loading ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin" /> : <Search className="w-4 h-4 text-slate-400" />}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); runSearch(e.target.value); }}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          placeholder="Search everything — CRM, tasks, calendar, docs..."
          className="bg-transparent outline-none text-sm flex-1 placeholder:text-slate-400"
          data-testid="topbar-search"
        />
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full mt-2 w-[28rem] right-0 bg-white border border-slate-200 rounded-xl shadow-xl max-h-[70vh] overflow-y-auto z-50" data-testid="global-search-results">
          {!loading && groups.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="global-search-no-results">No Results Found</div>
          )}
          {groups.map((g) => (
            <div key={g.category} className="py-1">
              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-magenta">{g.category}</div>
              {g.items.map((item) => (
                <button
                  key={`${g.category}-${item.id}`}
                  onClick={() => go(item.link)}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 transition"
                  data-testid={`search-result-${item.id}`}
                >
                  <div className="text-sm font-medium text-navy truncate">{item.title}</div>
                  {item.subtitle && <div className="text-xs text-slate-500 truncate">{item.subtitle}</div>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
